/*
Get apartment from API. Just a bit of caching magic here to prevent multiple requests to same resource.
 */

import axios from 'axios';

import globals from '../lib/globals';

export const notFound = 'notFound';

// Cached apartments / promises for apartments. Key is apartment UUID and value is apartment/promise.
let cache = {};

async function getSingle(uuid) {
  try {
    const response = await axios.get(globals.urlIntegrationApi + '/public/apartment/' + uuid);
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      return notFound;
    }
    return null;
  }
}

async function getMultiple(uuids) {
  const response = await axios.post(globals.urlIntegrationApi + '/public/apartments', {apartments: uuids});
  return response.data;
}

// Request a single apartment and caches the promise / apartment for future use. Uses cached result if available.
async function handleSingle(uuid) {
  if (cache.hasOwnProperty(uuid)) {
    return cache[uuid];
  }
  const promise = getSingle(uuid);

  cache[uuid] = promise;

  return await promise;
}

// Request multiple apartments. Uses cached apartments when available to reduce number of requested apartments or skip
// the request completely. Creates a separate promise (held in cache) for each apartment.
async function handleMultiple(uuids) {
  const uuidsCached = uuids.filter(uuid => cache.hasOwnProperty(uuid));
  const uuidsNew = uuids.filter(uuid => !uuidsCached.includes(uuid)); // new = not found in cache

  // Case 1: All apartments are found in cache -> skip request and just return values from cache.
  const apartmentsCachedPromises = uuidsCached.map(uuid => cache[uuid]);
  if (uuidsNew.length === 0) {
    return await Promise.all(apartmentsCachedPromises);
  }

  // Case 2: Some (or all) of the apartments are new -> get these in a single request.
  const apartmentsNewPromise = getMultiple(uuidsNew);

  // Create a separate promise for each new apartment in cache. These individual promises are resolved when the single
  // request is resolved.
  const getSingle = async (uuid) => {
    const apartments = await apartmentsNewPromise;
    return apartments.find(apartment => apartment.mpc_asuntoid === uuid) || notFound;
  };
  cache = Object.assign(
    cache,
    ...uuidsNew.map(uuid => ({[uuid]: getSingle(uuid)}))
  );

  // Combine previously cached and new apartments to a single response. Apartments not found are filtered out.
  return [
    ...(await Promise.all(apartmentsCachedPromises)),
    ...(await apartmentsNewPromise),
  ].filter(apartment => apartment !== notFound);
}

// Request apartment(s). Each individual apartment is cached (by UUID) and future requests will use cached values when
// available.
export default (value) => {
  if (Array.isArray(value)) {
    return handleMultiple(value)
  }
  return handleSingle(value);
}
